import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  useGetApproverReport,
  useAIFMUpdateData,
  useGetAIFMJurisdictions,
  useGetAIFMReport,
  useGetJurisdictions,
  useGetLoadAllData,
  useGetReportSchema,
  useUpdateData
} from '../../../../services/report.service';
import {
  filterArrayByFieldsForMultipleTable,
  filterArrayByFieldsNotEmptyValue,
  getDefaultValueByType,
  getKeyByType,
  getTypeFromSchema,
  validateFieldRules
} from '../utils/functions';
import {
  assetTypes,
  macroAssetTypes,
  subAssetTypeFields,
  subAssetTypes,
  turnoverAssetTypes
} from '../../../../consts/assetTypes';
import { strategyTableVariants } from '../consts/tableSections';
import { buildModifiedPayload } from '../../utils/savePayload';
import { REPORT_TYPE_IDS } from '../../consts/reportTypes';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConfirmSaveToast } from '../../components/ConfirmSaveToast';
import commonStore from '../../../../store/common';
import useHistoryState from '../../../../hooks/useHistoryState';
import { useUserGroupsCurrentUserAdminState } from '../../../../services/user.service';
import { useGetJurisdictionsApprover } from '../../../../services/jurisdiction.service';
import { useLoadDataToSnowflake } from '../../../../services/workflow.services';
import { useAutomateReportGeneration } from '../../../../services/automation.service';
import { useConvertReport } from '../../../../services/period.service';
import { queryClient } from '../../../../config/queryClient';
import reportStore from '../../store';
import { reaction } from 'mobx';
import { extractApiError } from '../../../../utils';
import { syncFundsByVisibleTableKeys } from '../../utils/syncFundsByVisibleTableKeys';

function addItemToArray(arr, index, newItem) {
  return [...arr.slice(0, index + 1), newItem, ...arr.slice(index + 1)];
}

export const useReport = ({
  fundId,
  isStaticTemplate,
  period,
  periodId,
  setSearchParams,
  isAIF001,
  isMultiFunds,
  isLockedPeriod,
  isConversionReportType
}) => {
  const navigate = useNavigate();
  const { reportingMemberState, workflowId } = useParams();
  const [currentDataKey, setCurrentDataKey] = useState();
  const [previousFunds, setPreviousFunds] = useState({});
  const [schema, setSchema] = useState({});
  const [searchParams] = useSearchParams();
  const jurisdictionId = searchParams.get('jurisdictionId');
  const [hasChanges, setHasChanges] = useState(false);

  const { data: AIFMjurisdictions } = useGetAIFMJurisdictions({
    periodId,
    queryParams: {
      enabled: !!period && !!jurisdictionId
    }
  });

  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );

  const conversionParse = useConvertReport();

  const currentPeriod = AIFMjurisdictions?.find(
    item => item.reporting_member_state === reportingMemberState.toUpperCase()
  );

  const { data: reportJurisdictions } = useGetJurisdictions({
    periodId,
    queryParams: {
      enabled: !!period && !jurisdictionId
    }
  });

  const { data: user, isLoading: isLoadingUser } = useUserGroupsCurrentUserAdminState();
  const isViewer = useMemo(() => {
    if (user?.role === 'Approver') {
      commonStore.setIsViewer(true);
      return true;
    }

    return false;
  }, [user]);

  const { data: jurisdictionsApprover } = useGetJurisdictionsApprover({
    periodId,
    queryParams: {
      enabled: !!period && !!isViewer
    }
  });

  // const jurisdictions = jurisdictionId ? AIFMjurisdictions : reportJurisdictions;

  const jurisdictions = isViewer
    ? jurisdictionsApprover
    : jurisdictionId
      ? AIFMjurisdictions
      : reportJurisdictions;

  const {
    state: { funds },
    set,
    undo,
    setInitialState,
    redo,
    canRedo,
    canUndo
  } = useHistoryState({
    funds: []
  });

  const onSaveResult = result => {
    let res = [];
    if (isComparisonReport) {
      res = isAIF001
        ? [
            {
              ...(result.current_fund || result),
              previous_fund: result.previous_fund,
              difference: result.difference || {}
            }
          ]
        : result.map(item => ({
            ...item.current_fund,
            previous_fund: item.previous_fund,
            difference: item.difference
          }));
    } else {
      res = isAIF001 ? [result] : result;
    }

    if (isAIF001 || jurisdictionId) {
      setCurrentDataKey('AIFOO1');
    } else {
      const isStaleDataKey = currentDataKey && !res.some(item => item?.fund_id === currentDataKey);
      if (
        (!currentDataKey || currentDataKey === 'AIFOO1' || isStaleDataKey) &&
        !fundId &&
        !isAIF001
      ) {
        setCurrentDataKey(res[0]?.fund_id);
        setSearchParams(
          prev => {
            prev.set('fund_id', res[0]?.fund_id);
            return prev;
          },
          { replace: true }
        );
        // setSearchParams({ fund_id: res[0].fund_id });
      }
    }

    const _funds = {};

    // TO DO: refactor -> simplify logic , decrease nested conditions
    res.forEach(item => {
      if (!isStaticTemplate) {
        const requiredTables = {};
        item.required_tables.forEach(item => {
          requiredTables[item.table_name] = item;
        });
        _funds[isAIF001 ? 'AIFOO1' : item.fund_id] = {
          ...item,
          visibleTableKeys: Object.keys(item.fund_data || {}),
          required_tables: requiredTables
        };
      } else {
        _funds[isAIF001 ? 'AIFOO1' : item.fund_id] = {
          ...item,
          visibleTableKeys: Object.keys(item.fund_data || {})
        };
      }
    });

    const buildBaseRow = (fund, tableKey, value = {}, extraFields = {}) => ({
      ...schema[tableKey].defaultValues,
      period_id: periodId,
      fund_name: fund.fund_name,
      fund_id: fund.fund_id,
      visibleTableKeys: fund.visibleTableKeys || [],
      is_template: isStaticTemplate,
      ...extraFields,
      ...value
    });

    const normalizeSingleRow = (fund, tableKey) => {
      const value = fund.fund_data[tableKey]?.[0] || {};
      fund.fund_data[tableKey] = [buildBaseRow(fund, tableKey, value)];
    };

    const normalizeRankedRows = (fund, tableKey, count) => {
      fund.fund_data[tableKey] = Array.from({ length: count }, (_, index) => {
        const ranking = `${index + 1}`;
        const value = fund.fund_data[tableKey]?.find(item => item.ranking === ranking) || {};
        return buildBaseRow(fund, tableKey, value, { ranking });
      });
    };

    const normalizeLabeledRows = (fund, tableKey, labels, labelKey) => {
      fund.fund_data[tableKey] = labels.map(label => {
        const value = fund.fund_data[tableKey]?.find(item => item[labelKey] === label) || {};
        return buildBaseRow(fund, tableKey, value, { [labelKey]: label });
      });
    };

    const rankedTableSizes = {
      BORROWING_SOURCE: 5,
      TOP_FIVE_COUNTRIES_EXPOSURE_EXCLUDE_CCP: 5,
      TOP_FIVE_COUNTRIES_COUNTERPARTY_EXPOSURE: 5,
      MAIN_INSTRUMENTS: 5,
      AIFM_FIVE_PRINCIPAL_MARKET: 5,
      AIFM_PRINCIPAL_INSTRUMENT: 5,
      CCP_EXPOSURE: 3,
      PRINCIPAL_MARKETS: 3,
      PRINCIPAL_EXPOSURES: 10,
      MOST_IMPORTANT_PORTFOLIO: 5
    };

    const labeledTableConfigs = {
      VALUE_FOR_YEARS_RISK_MEASURE: {
        labels: ['NET_DV01', 'NET_CS01'],
        labelKey: 'risk_measure_type'
      },
      HISTORICAL_PROFILE: {
        labels: ['GROSS', 'NET', 'NAV_CHANGE', 'SUBSCRIPTION', 'REDEMPTION'],
        labelKey: 'transaction_type'
      },
      MARKET_RISK_MEASURE: {
        labels: ['NET_EQTY_DELTA', 'NET_FX_DELTA', 'NET_CTY_DELTA'],
        labelKey: 'risk_measure_type'
      }
    };

    Object.keys(_funds).forEach(fundKey => {
      const currentFund = _funds[fundKey];

      currentFund.isSelected = true;

      if (isComparisonReport) {
        currentFund.previous_fund = {
          fund_id: currentFund.previous_fund?.fund_id || currentFund.fund_id,
          fund_name: currentFund.previous_fund?.fund_name || currentFund.fund_name,
          period: currentFund.previous_fund?.period || '',
          year: currentFund.previous_fund?.year || '',
          fund_data: currentFund.previous_fund?.fund_data || {},
          visibleTableKeys: Object.keys(currentFund.previous_fund?.fund_data || {})
        };
      }
    });

    syncFundsByVisibleTableKeys(_funds, { includePreviousFund: isComparisonReport });

    Object.keys(_funds).forEach(fundKey => {
      const currentFund = _funds[fundKey];

      const previousFund = currentFund.previous_fund;

      Object.keys(currentFund.fund_data).forEach(tableKey => {
        const normalizeForComparison = normalizeFn => {
          normalizeFn(currentFund);
          if (isComparisonReport && previousFund) {
            normalizeFn(previousFund);
          }
        };

        if (
          tableKey === 'INVESTOR_LIQUIDITY_PROFILE' ||
          currentFund.fund_data[tableKey].length === 0 ||
          (isComparisonReport && previousFund?.fund_data?.[tableKey]?.length === 0)
        ) {
          normalizeForComparison(fund => normalizeSingleRow(fund, tableKey));
        }

        if (rankedTableSizes[tableKey]) {
          normalizeForComparison(fund =>
            normalizeRankedRows(fund, tableKey, rankedTableSizes[tableKey])
          );
        }

        if (labeledTableConfigs[tableKey]) {
          const { labels, labelKey } = labeledTableConfigs[tableKey];
          normalizeForComparison(fund => normalizeLabeledRows(fund, tableKey, labels, labelKey));
        }

        if (tableKey === 'INDIVIDUAL_EXPOSURES') {
          const _tables = [];
          const _previous_tables = [];
          const groupedData = {};

          Object.keys(macroAssetTypes).forEach(key => {
            groupedData[key] = {};
          });

          Object.keys(assetTypes).forEach(key => {
            const macroKey = key.split('_')[0];
            if (groupedData[macroKey]) {
              groupedData[macroKey][key] = {};
            }
          });

          Object.keys(subAssetTypes).forEach(key => {
            const assetKey = key.split('_').slice(0, 2).join('_');
            const macroKey = key.split('_')[0];
            if (groupedData[macroKey] && groupedData[macroKey][assetKey]) {
              groupedData[macroKey][assetKey][key] = subAssetTypeFields[key];
            }
          });

          Object.keys(groupedData).forEach(macroAssetType => {
            Object.keys(groupedData[macroAssetType]).forEach(assetType => {
              Object.keys(groupedData[macroAssetType][assetType]).forEach(subAssetType => {
                const value =
                  _funds[fundKey].fund_data[tableKey].find(
                    item => item.sub_asset_type === subAssetType
                  ) || {};

                _tables.push({
                  period_id: periodId,
                  fund_name: _funds[fundKey].fund_name,
                  fund_id: _funds[fundKey].fund_id,
                  visibleTableKeys: _funds[fundKey].visibleTableKeys || [],
                  is_template: isStaticTemplate,
                  macro_asset_type: macroAssetType,
                  asset_type: assetType,
                  sub_asset_type: subAssetType,
                  ...value
                });

                if (isComparisonReport) {
                  const _previous_value =
                    _funds[fundKey].previous_fund.fund_data[tableKey].find(
                      item => item.sub_asset_type === subAssetType
                    ) || {};

                  _previous_tables.push({
                    period_id: periodId,
                    fund_name: _funds[fundKey].previous_fund.fund_name,
                    fund_id: _funds[fundKey].previous_fund.fund_id,
                    visibleTableKeys: _funds[fundKey].previous_fund.visibleTableKeys || [],
                    is_template: isStaticTemplate,
                    macro_asset_type: macroAssetType,
                    asset_type: assetType,
                    sub_asset_type: subAssetType,
                    ..._previous_value
                  });
                }
              });
            });
          });

          _funds[fundKey].fund_data[tableKey] = _tables;
          if (isComparisonReport) {
            _funds[fundKey].previous_fund.fund_data[tableKey] = _previous_tables;
          }
        }
        if (tableKey === 'TURNOVER') {
          const _tables = [];
          const _previous_tables = [];

          Object.keys(turnoverAssetTypes).forEach(subAssetType => {
            const value =
              _funds[fundKey].fund_data[tableKey].find(
                item => item.sub_asset_type === subAssetType
              ) || {};

            _tables.push({
              period_id: periodId,
              fund_name: _funds[fundKey].fund_name,
              fund_id: _funds[fundKey].fund_id,
              visibleTableKeys: _funds[fundKey].visibleTableKeys || [],
              is_template: isStaticTemplate,
              sub_asset_type: subAssetType,
              ...value
            });

            if (isComparisonReport) {
              const _previous_value =
                _funds[fundKey].previous_fund.fund_data[tableKey].find(
                  item => item.sub_asset_type === subAssetType
                ) || {};

              _previous_tables.push({
                period_id: periodId,
                fund_name: _funds[fundKey].previous_fund.fund_name,
                fund_id: _funds[fundKey].previous_fund.fund_id,
                visibleTableKeys: _funds[fundKey].previous_fund.visibleTableKeys || [],
                is_template: isStaticTemplate,
                sub_asset_type: subAssetType,
                ..._previous_value
              });
            }
          });

          const globalErrors = _funds[fundKey].fund_data[tableKey]
            .filter(
              item =>
                !Object.keys(turnoverAssetTypes).includes(item.sub_asset_type) &&
                item.errors?.sub_asset_type
            )
            .map(item => item.errors.sub_asset_type);

          if (globalErrors.length > 0) {
            _tables.forEach(item => {
              const existingGeneral = item?.errors?.general || [];
              item.errors = {
                ...item.errors,
                general: [...existingGeneral, ...globalErrors]
              };
            });
          }

          _funds[fundKey].fund_data[tableKey] = _tables;
          if (isComparisonReport) {
            _funds[fundKey].previous_fund.fund_data[tableKey] = _previous_tables;
          }
        }
      });
    });

    commonStore.setFundsSize(Object.keys(_funds).length);

    setInitialState({
      funds: _funds
    });
    // if (!isAIF001) {
    //   setFundsForAIF(Object.keys(_funds));
    // }
    setPreviousFunds(_funds);
    toast.remove();
  };

  const report = useGetLoadAllData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      report_type: isAIF001 ? REPORT_TYPE_IDS.AIF001 : REPORT_TYPE_IDS.AIF002,
      reporting_member_state: reportingMemberState.toUpperCase(),
      comparison_mode: isComparisonReport
    },
    queryParams: {
      enabled:
        !!(Object.keys(schema).length > 0) &&
        !isViewer &&
        !(
          period.data?.modeling_status === 'Not started' &&
          period.data?.report_progress_status === 'Not started'
        ) &&
        !jurisdictionId,
      onSuccess: onSaveResult,
      cacheTime: 0,
      staleTime: 0
    }
  });

  const approverReport = useGetApproverReport({
    periodId,
    params: {
      is_template: isStaticTemplate,
      report_type: isAIF001 ? REPORT_TYPE_IDS.AIF001 : REPORT_TYPE_IDS.AIF002,
      reporting_member_state: reportingMemberState.toUpperCase(),
      comparison_mode: isComparisonReport
    },
    queryParams: {
      enabled: !!(Object.keys(schema).length > 0) && isViewer,
      onSuccess: onSaveResult,
      cacheTime: 0,
      staleTime: 0
    }
  });

  const aifmReport = useGetAIFMReport({
    periodId,
    params: {
      jurisdiction_id: jurisdictionId,
      is_template: isStaticTemplate
    },
    queryParams: {
      onSuccess: onSaveResult,
      enabled: !!(Object.keys(schema).length > 0) && !!jurisdictionId
    }
  });

  const isLoading = isViewer
    ? approverReport.isLoading
    : jurisdictionId
      ? aifmReport.isLoading
      : report.isLoading;
  const refetch = isViewer
    ? approverReport.refetch
    : jurisdictionId
      ? aifmReport.refetch
      : report.refetch;
  const isFetching = isViewer
    ? approverReport.isFetching
    : jurisdictionId
      ? aifmReport.isFetching
      : report.isFetching;
  // The active query's error, so ReportContainer can render an in-page
  // notification when the report fetch itself fails (500 etc.). Mirrors the
  // isLoading/refetch/isFetching branching above.
  const reportLoadError = isViewer
    ? approverReport.error
    : jurisdictionId
      ? aifmReport.error
      : report.error;
  const isReportLoadError = isViewer
    ? approverReport.isError
    : jurisdictionId
      ? aifmReport.isError
      : report.isError;

  const loadDataToSnowflake = useLoadDataToSnowflake();
  const automateReportGeneration = useAutomateReportGeneration();

  useEffect(() => {
    if (
      period.data?.modeling_status === 'Not started' &&
      period.data?.report_progress_status === 'Not started' &&
      !isConversionReportType &&
      !isViewer &&
      !isStaticTemplate
    ) {
      // Initial periods (template_type === 'sratch') go through the
      // load-data-to-snowflake pipeline; automation periods use the
      // automation-report endpoint.
      const isInitialPeriod = period.data?.template_type === 'sratch';
      const promise = isInitialPeriod
        ? loadDataToSnowflake.mutateAsync(periodId, {
            onSuccess: () => {
              refetch();
              period.refetch();
            }
          })
        : automateReportGeneration.mutateAsync(
            { period_id: periodId },
            {
              onSuccess: () => {
                refetch();
                period.refetch();
              }
            }
          );
      toast.promise(promise, {
        loading: 'Generating report...',
        success: 'Рeport was successfully generated',
        error: 'Something was wrong while generating report'
      });
    } else if (isConversionReportType && period.data?.report_progress_status === 'Not started') {
      toast.promise(
        conversionParse.mutateAsync(
          { periodId, workflowId },
          {
            onSuccess: () => {
              refetch();
              period.refetch();
            }
          }
        ),
        {
          loading: 'Parsing report...',
          success: 'Рeport was successfully parsed',
          error: 'Something was wrong while parsing report'
        }
      );
    }
  }, [period.data, periodId, workflowId, isConversionReportType, isStaticTemplate]);

  const { mutateAsync: mutateAsyncReport, isLoading: isLoadingDataReport } = useUpdateData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      reporting_member_state: reportingMemberState.toUpperCase()
    }
  });

  const { mutateAsync: mutateAsyncAIFM, isLoading: isLoadingDataAIFM } = useAIFMUpdateData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      jurisdiction_id: jurisdictionId
    }
  });

  const mutateAsync = jurisdictionId ? mutateAsyncAIFM : mutateAsyncReport;
  const isLoadingData = jurisdictionId ? isLoadingDataAIFM : isLoadingDataReport;

  const { isLoading: isLoadingSchema } = useGetReportSchema({
    periodId: jurisdictionId ? currentPeriod?.period_id : periodId,
    params: {
      reporting_member_state: reportingMemberState.toUpperCase()
    },
    queryParams: {
      enabled: jurisdictionId ? !!currentPeriod : !!periodId,
      onSuccess: res => {
        const _res = {};

        Object.keys(res).forEach(key => {
          const defaultValues = {};
          const types = {};
          Object.keys(res[key].properties).forEach(element => {
            defaultValues[element] = getDefaultValueByType(res[key].properties[element]);
            const _type = getTypeFromSchema(res[key].properties[element]);
            let additionalValues = {};
            if (_type === 'select') {
              additionalValues.options =
                res[key].properties[element].rules.array.enum ||
                res[key].properties[element].rules.string.enum;
            }
            types[element] = {
              type: _type,
              required: isStaticTemplate ? false : res[key].required.includes(element),
              key: getKeyByType(_type),
              has_drill_down_feature: res[key].properties[element].has_drill_down_feature || false,
              rules: res[key].properties[element].rules,
              ...additionalValues
            };
          });
          _res[key] = {
            defaultValues: defaultValues,
            types
          };
        });

        setSchema(_res);
      },
      onError: err => {
        toast.error(`Error fetching schema at useGetReportSchema: ${extractApiError(err)}`);
      }
    }
  });

  useEffect(() => {
    if (fundId) {
      setCurrentDataKey(fundId);
    }
  }, [fundId, isAIF001]);

  useEffect(() => {
    const handleBeforeUnload = async e => {
      if (!hasChanges || isLockedPeriod) return;

      const confirmationMessage = 'You have unsaved data. Are you sure want to proceed?';
      e.returnValue = confirmationMessage;
      return confirmationMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges, isLockedPeriod]);

  const onChangeFund = async (key, value, fundId) => {
    const _funds = JSON.parse(JSON.stringify(funds));
    const tableTypes = schema[key]?.types || {};
    const validatedValue = value.map(row => {
      const rowErrors = { ...(row.errors || {}) };
      Object.keys(tableTypes).forEach(fieldName => {
        const error = validateFieldRules(row[fieldName], tableTypes[fieldName]?.rules);

        if (error !== undefined) {
          if (error) {
            rowErrors[fieldName] = { msg: error };
          } else {
            delete rowErrors[fieldName];
          }
        }
      });
      return { ...row, errors: rowErrors };
    });

    _funds[fundId || currentDataKey].fund_data[key] = validatedValue;
    set({
      funds: _funds
    });
    setHasChanges(true);
  };

  const addRow = (key, index) => {
    const _funds = JSON.parse(JSON.stringify(funds));

    Object.keys(funds).forEach(fundId => {
      _funds[fundId].fund_data[key] = addItemToArray(
        _funds[isMultiFunds ? fundId : currentDataKey].fund_data[key],
        index,
        {
          ...schema[key].defaultValues,
          period_id: periodId,
          fund_name: _funds[isMultiFunds ? fundId : currentDataKey].fund_name,
          fund_id: _funds[isMultiFunds ? fundId : currentDataKey].fund_id
        }
      );
    });

    set({
      funds: _funds
    });

    // setFunds(_funds);
    setHasChanges(true);
  };

  const onDeleteRow = (key, index) => {
    const _funds = JSON.parse(JSON.stringify(funds));

    Object.keys(funds).forEach(fundId => {
      _funds[fundId].fund_data[key] = _funds[fundId].fund_data[key].map((item, ind) =>
        ind === index
          ? {
              ...item,
              is_deleted: true
            }
          : item
      );
    });

    set({
      funds: _funds
    });
  };

  const onClearStaticData = () => {
    const _funds = JSON.parse(JSON.stringify(funds));
    if (isMultiFunds) {
      Object.keys(funds).forEach(_fundId => {
        const values = JSON.parse(JSON.stringify(funds[_fundId]));
        Object.keys(values.fund_data).map(tableName => {
          const table = values.fund_data[tableName];
          table.forEach(item => {
            Object.keys(item).forEach(fieldName => {
              // TO DO: refactor conditions -> create constant vars for strings
              if (
                tableName === 'MAIN_INSTRUMENTS' ||
                tableName === 'MOST_IMPORTANT_PORTFOLIO' ||
                tableName === 'DOMINANT_INFLUENCES'
              ) {
                if (
                  fieldName !== 'period_id' &&
                  fieldName !== 'fund_id' &&
                  fieldName !== 'fund_name' &&
                  fieldName !== 'id'
                ) {
                  item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
                }
              } else {
                if (
                  fieldName !== 'period_id' &&
                  fieldName !== 'fund_id' &&
                  fieldName !== 'fund_name' &&
                  fieldName !== 'sub_asset_type' &&
                  fieldName !== 'asset_type' &&
                  fieldName !== 'macro_asset_type' &&
                  fieldName !== 'transaction_type' &&
                  fieldName !== 'risk_measure_type' &&
                  fieldName !== 'id'
                ) {
                  item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
                }
              }
            });
          });
        });
        _funds[_fundId] = values;
      });
    } else {
      const values = JSON.parse(JSON.stringify(funds[currentDataKey]));
      Object.keys(values.fund_data).map(tableName => {
        const table = values.fund_data[tableName];
        table.forEach(item => {
          Object.keys(item).forEach(fieldName => {
            // TO DO: refactor conditions -> create constant vars for strings
            if (
              tableName === 'MAIN_INSTRUMENTS' ||
              tableName === 'MOST_IMPORTANT_PORTFOLIO' ||
              tableName === 'DOMINANT_INFLUENCES'
            ) {
              if (
                fieldName !== 'period_id' &&
                fieldName !== 'fund_id' &&
                fieldName !== 'fund_name' &&
                fieldName !== 'id'
              ) {
                item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
              }
            } else {
              if (
                fieldName !== 'period_id' &&
                fieldName !== 'fund_id' &&
                fieldName !== 'fund_name' &&
                fieldName !== 'sub_asset_type' &&
                fieldName !== 'asset_type' &&
                fieldName !== 'macro_asset_type' &&
                fieldName !== 'transaction_type' &&
                fieldName !== 'risk_measure_type' &&
                fieldName !== 'id'
              ) {
                item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
              }
            }
          });
        });
      });

      _funds[currentDataKey] = values;
    }

    set({
      funds: _funds
    });
  };

  const onChangeDataKey = async value => {
    if (!isViewer) {
      if ((isFetching && !isLoading) || isLoadingData) return;
      if (hasChanges && !isLockedPeriod) {
        toast.remove();
        toast.custom(
          () => (
            <ConfirmSaveToast
              onSave={() => {
                toast.remove();
                onSave({
                  callbackFn: function () {
                    setSearchParams(
                      prev => {
                        prev.set('fund_id', value);
                        return prev;
                      },
                      { replace: true }
                    );
                    // setSearchParams({ fund_id: value });
                    setCurrentDataKey(value);
                  }
                });
              }}
              onDiscard={() => {
                // setSearchParams({ fund_id: value });
                setSearchParams(
                  prev => {
                    prev.set('fund_id', value);
                    return prev;
                  },
                  { replace: true }
                );
                setCurrentDataKey(value);
                revertDiscardChanges();
                toast.remove();
              }}
            />
          ),
          {
            duration: 100000
          }
        );
        return;
      }
    }

    // setSearchParams({ fund_id: value });
    setSearchParams(
      prev => {
        prev.set('fund_id', value);
        return prev;
      },
      { replace: true }
    );
    setCurrentDataKey(value);
  };

  const onSave = ({ callbackFn } = {}) => {
    const payload = buildModifiedPayload({
      funds,
      baselineFunds: previousFunds,
      schema,
      isMultiFunds,
      isAIF001,
      currentDataKey,
      strategyTableKeys: strategyTableVariants.map(item => item.key),
      filterNotEmpty: filterArrayByFieldsNotEmptyValue,
      filterMultiple: filterArrayByFieldsForMultipleTable
    });

    toast.promise(mutateAsync(payload), {
      loading: 'Saving...',
      success: () => {
        //setHasChanges(false);
        toast.loading('Validating...');
        async function loadData() {
          const result = await refetch();
          queryClient.refetchQueries(['GET_REPORT_ERRORS']);
          setHasChanges(false);
          if (result.isSuccess && callbackFn) callbackFn();
        }
        loadData();
        return 'Saved successfuly!';
      },
      error: err => `Error while saving the report. ${extractApiError(err)}`
    });
  };

  const onNavigateOtherReport = async value => {
    if (reportingMemberState === value.reporting_member_state.toLowerCase()) {
      return;
    }
    if (!isViewer) {
      if ((isFetching || !isLoading) && isLoadingData) return;
      if (hasChanges && !isLockedPeriod) {
        toast.remove();
        toast.custom(
          () => (
            <ConfirmSaveToast
              onSave={() => {
                toast.remove();
                onSave({
                  callbackFn: function () {
                    navigate(
                      `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}/${workflowId}/${periodId}${searchParams.get('reportType') === 'grouped' ? `?reportType=grouped${jurisdictionId ? `&jurisdictionId=${value.id}` : ''}` : ''}`
                    );
                  }
                });
              }}
              onDiscard={() => {
                navigate(
                  `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}/${workflowId}/${periodId}${searchParams.get('reportType') === 'grouped' ? `?reportType=grouped${jurisdictionId ? `&jurisdictionId=${value.id}` : ''}` : ''}`
                );
                revertDiscardChanges();
                toast.remove();
              }}
            />
          ),
          {
            duration: 100000
          }
        );
        return;
      }
    }

    navigate(
      `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}/${workflowId}/${periodId}${searchParams.get('reportType') === 'grouped' ? `?reportType=grouped${jurisdictionId ? `&jurisdictionId=${value.id}` : ''}` : ''}`
    );
  };

  const onChangeSelectedReportType = async val => {
    if (!isViewer) {
      if ((isFetching && !isLoading) || isLoadingData) return;
      if (hasChanges && !isLockedPeriod) {
        toast.remove();
        toast.custom(
          () => (
            <ConfirmSaveToast
              onSave={() => {
                toast.remove();
                onSave({
                  callbackFn: function () {
                    setSearchParams(
                      prev => {
                        prev.set('type', val);
                        return prev;
                      },
                      { replace: true }
                    );
                  }
                });
              }}
              onDiscard={() => {
                setSearchParams(
                  prev => {
                    prev.set('type', val);
                    return prev;
                  },
                  { replace: true }
                );
                revertDiscardChanges();
                toast.remove();
              }}
            />
          ),
          {
            duration: 100000
          }
        );
        return;
      }
    }
    setSearchParams(
      prev => {
        prev.set('type', val);
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSelectedFunds = async fundId => {
    if (fundId === 'all') {
      if (Object.values(funds).every(fund => fund.isSelected)) {
        const newFunds = Object.keys(funds).reduce((acc, key) => {
          if (key === currentDataKey) {
            acc[key] = {
              ...funds[key],
              isSelected: true
            };
          } else {
            acc[key] = {
              ...funds[key],
              isSelected: false
            };
          }
          return acc;
        }, {});
        setInitialState({ funds: newFunds });
      } else {
        const newFunds = Object.keys(funds).reduce((acc, key) => {
          acc[key] = {
            ...funds[key],
            isSelected: true
          };
          return acc;
        }, {});
        setInitialState({ funds: newFunds });
      }
      return;
    }

    const toggleFundSelection = () => {
      setInitialState({
        funds: {
          ...funds,
          [fundId]: {
            ...funds[fundId],
            isSelected: !funds[fundId].isSelected
          }
        }
      });
    };

    const isCurrentlyDisplayedFund = fundId === currentDataKey;
    const currentFund = funds[fundId];
    const isCurrentlySelected = currentFund.isSelected;

    if (isCurrentlyDisplayedFund) {
      // Toggling the currently displayed fund
      if (isCurrentlySelected) {
        // Deselecting the currently displayed fund - switch to another if available
        const otherSelectedFunds = Object.values(funds).filter(
          fund => fund.isSelected && fund.fund_id !== fundId
        );

        if (otherSelectedFunds.length > 0) {
          // Switch to the first available selected fund before deselecting
          const nextFund = otherSelectedFunds[0];
          setCurrentDataKey(nextFund.fund_id);
          setSearchParams(
            prev => {
              prev.set('fund_id', nextFund.fund_id);
              return prev;
            },
            { replace: true }
          );
          toggleFundSelection();
        }
        // If no other funds are selected, don't allow deselecting the last one
      } else {
        // Selecting the currently displayed fund - always allow
        toggleFundSelection();
      }
    } else {
      // Toggling a fund that's not currently displayed
      if (isCurrentlySelected) {
        // Deselecting a non-displayed fund - ensure at least one remains selected
        const otherSelectedFunds = Object.values(funds).filter(
          fund => fund.isSelected && fund.fund_id !== fundId
        );

        if (otherSelectedFunds.length >= 1) {
          // Allow deselection if at least one other fund will remain selected
          toggleFundSelection();
        }
        // If this is the only other selected fund, don't allow deselecting
      } else {
        // Selecting a fund - always allow
        toggleFundSelection();
      }
    }
  };

  const revertDiscardChanges = useCallback(() => {
    if (Object.keys(previousFunds || {}).length > 0) {
      setInitialState({
        funds: JSON.parse(JSON.stringify(previousFunds))
      });
    }
    setHasChanges(false);
  }, [previousFunds, setInitialState]);

  const withUnsavedChangesGuard = useCallback(
    action => {
      if (!isViewer) {
        if ((isFetching && !isLoading) || isLoadingData) return;
        if (hasChanges && !isLockedPeriod) {
          toast.remove();
          toast.custom(
            () => (
              <ConfirmSaveToast
                onSave={() => {
                  toast.remove();
                  onSave({
                    callbackFn: function () {
                      action?.();
                    }
                  });
                }}
                onDiscard={() => {
                  revertDiscardChanges();
                  toast.remove();
                  action?.();
                }}
              />
            ),
            {
              duration: 100000
            }
          );
          return;
        }
      }

      action?.();
    },
    [
      hasChanges,
      isLockedPeriod,
      isViewer,
      isFetching,
      isLoading,
      isLoadingData,
      onSave,
      revertDiscardChanges
    ]
  );

  const selectedFunds = Object.values(funds)
    .filter(fund => fund.isSelected)
    .reduce((acc, fund) => {
      acc[fund.fund_id] = fund;
      return acc;
    }, {});

  return {
    funds: isAIF001 ? funds : selectedFunds,
    allFunds: funds,
    schema,
    isLoading: loadDataToSnowflake.isLoading || isLoading,
    hasChanges,
    previousFunds,
    currentDataKey,
    isLoadingData,
    addRow,
    onSave,
    refetch,
    onDeleteRow,
    onChangeFund,
    onChangeDataKey,
    onClearStaticData,
    isLoadingSchema,
    jurisdictions,
    onNavigateOtherReport,
    onChangeSelectedReportType,
    isViewer,
    canRedo,
    canUndo,
    undo,
    redo,
    isLoadingUser,
    onChangeSelectedFunds,
    withUnsavedChangesGuard,
    reportLoadError,
    isReportLoadError
  };
};
